import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { ToastContainer, toast } from 'react-toastify';
import axios from "axios";
import * as XLSX from 'xlsx';
import excelIcon from "../../assets/icons/excel.png";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFilePdf } from "react-icons/fa";
import AppDataGrid from "../../components/common/AppDataGrid";

const SchoolStrength = () => {
  const userData = sessionStorage.getItem('user');
  const [classes, setClasses] = useState(null);
  const [sections, setSections] = useState(null);
  const [filteredSections, setFilteredSections] = useState([]);
  const [academic, setAcademic] = useState([]);
  const routeLocation = useLocation();
  const userObj = userData ? JSON.parse(userData) : {};
  const [form, setForm] = useState({
    class_id: "",
    section_id: "",
    academic_year_id: ""
  });
  const [strengthData, setStrengthData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // --- helpers for ratios (rounded integers) ---

  function gcd(a, b) {
    a = Math.abs(Number(a) || 0);
    b = Math.abs(Number(b) || 0);
    return b === 0 ? a : gcd(b, a % b);
  }

  const getBoysGirlsRatio = (boys, girls) => {
    boys = Number(boys) || 0;
    girls = Number(girls) || 0;

    if (boys === 0 && girls === 0) return "";
    if (girls === 0) return `${boys}:0`;

    const divisor = gcd(boys, girls) || 1;
    const simplifiedBoys = Math.round(boys / divisor);
    const simplifiedGirls = Math.round(girls / divisor);

    return `${simplifiedBoys}:${simplifiedGirls}`;
  };

  const getTeacherStudentRatio = (boys, girls, teachers) => {
    boys = Number(boys) || 0;
    girls = Number(girls) || 0;
    teachers = Number(teachers) || 0;

    const totalStudents = boys + girls;

    if (teachers === 0 && totalStudents === 0) return "";
    if (totalStudents === 0) return `${teachers}:0`;

    return `${teachers}:${totalStudents}`;
  };

  // --- columns for AppDataGrid ---

  const columns = [
    {
      field: 'class_name',
      headerName: 'Class',
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
    },
    {
      field: 'section_name',
      headerName: 'Section',
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
    },
    {
      field: 'total_boys',
      headerName: 'Boys',
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) => Number(params.row.total_boys) || 0,
    },
    {
      field: 'total_girls',
      headerName: 'Girls',
      flex: 0.7,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) => Number(params.row.total_girls) || 0,
    },
    {
      field: 'total_teachers',
      headerName: 'Teachers',
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) => Number(params.row.total_teachers) || 0,
    },
    {
      field: 'boys_girls_ratio',
      headerName: 'Boys:Girls Ratio',
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) =>
        getBoysGirlsRatio(params.row.total_boys, params.row.total_girls),
    },
    {
      field: 'teacher_student_ratio',
      headerName: 'Teacher:Student Ratio',
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) =>
        getTeacherStudentRatio(
          params.row.total_boys,
          params.row.total_girls,
          params.row.total_teachers
        ),
    },
  ];

  const exportToPDF = () => {
    if (strengthData.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const doc = new jsPDF();
    doc.text("School Strength Report", 14, 10);

    const tableColumn = [
      "Class",
      "Section",
      "Boys",
      "Girls",
      "Teachers",
      "Boys:Girls Ratio",
      "Teacher:Student Ratio",
    ];
    const tableRows = [];

    strengthData.forEach((row) => {
      const boys = row.total_boys || 0;
      const girls = row.total_girls || 0;
      const teachers = row.total_teachers || 0;

      const boysGirlsRatio = getBoysGirlsRatio(boys, girls);
      const teacherStudentRatio = getTeacherStudentRatio(boys, girls, teachers);

      tableRows.push([
        row.class_name || "",
        row.section_name || "",
        boys,
        girls,
        teachers,
        boysGirlsRatio,
        teacherStudentRatio,
      ]);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save(`school_strength_report_${new Date().toISOString()}.pdf`);
  };

  const fetchStrengthData = async () => {
    if (!userObj.academic_year_id || !form.class_id || !form.section_id) return;
    setIsLoading(true);
    try {
      const response = await axios.post(baseUrl + "/strength/", {
        action: "FILTER",
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
        class_id: form.class_id,
        section_id: form.section_id
      });
      setStrengthData(response.data || []);
    } catch (error) {
      console.log("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.post(baseUrl + "/classes/", {
        action: "READ",
        school_id: userObj?.school_id || 0,
      });
      setClasses(response?.data?.filter(classItem => classItem.is_active === "Active") || []);
    } catch (error) {
      console.error("Error fetching classes!", error);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const response = await axios.post(baseUrl + "/Sections/", {
        action: "DROPDOWNREAD",
        school_id: userObj.school_id,
        class_id,
        academic_year_id: userObj.academic_year_id
      });
      const data = Array.isArray(response.data) ? response.data : [];
      const active = data.filter((item) => item.is_active?.toLowerCase() === "active");
      setSections(active);
      setFilteredSections(active);
    } catch (err) {
      console.error("Error fetching sections", err);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.post(baseUrl + "/AcademicYear/", {
        action: "CURRENTREAD",
        school_id: userObj?.school_id || 0,
      });
      setAcademic(response?.data || []);
    } catch (error) {
      console.error("Error fetching academic years!", error);
    }
  };

  useEffect(() => {
    fetchAcademicYears();
    fetchClasses();
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      [id]: value
    }));

    if (id === "class_id") {
      fetchSections(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await fetchStrengthData();
    } finally {
      setIsSubmitted(true);
      setIsLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!strengthData.length) {
      toast.error("No data available to export.");
      return;
    }

    const updatedStrengthData = strengthData.map(row => {
      const boys = row.total_boys || 0;
      const girls = row.total_girls || 0;
      const teachers = row.total_teachers || 0;

      return {
        Class: row.class_name,
        Section: row.section_name,
        Boys: boys,
        Girls: girls,
        Teachers: teachers,
        "Boys:Girls Ratio": getBoysGirlsRatio(boys, girls),
        "Teacher:Student Ratio": getTeacherStudentRatio(boys, girls, teachers),
      };
    });

    const ws = XLSX.utils.json_to_sheet(updatedStrengthData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Strength Data");
    XLSX.writeFile(wb, "school_strength_report.xlsx");
  };

  return (
    <div className='pageBody'>
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>School Strength</h6>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} lg={4} xxl={4}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Class
                      </Form.Label>
                      <Form.Select
                        id="class_id"
                        value={form.class_id}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select Class</option>
                        {(classes || [])
                          .filter((classItem) => classItem.is_active === "Active")
                          .map((classItem) => (
                            <option
                              key={classItem.class_id}
                              value={classItem.class_id}
                            >
                              {classItem.class_name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={12} md={6} lg={4} xxl={4}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Section
                      </Form.Label>
                      <Form.Select
                        id="section_id"
                        value={form.section_id || ""}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled hidden>Select Section</option>
                        {(filteredSections || [])
                          .filter((section) => section.is_active === "Active")
                          .map((section) => (
                            <option
                              key={section.section_id}
                              value={section.section_id}
                            >
                              {section.section_name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
                <Col xs={6} md={3} lg={2} xxl={2}>
                  <div className="d-flex justify-content-between mt-4" style={{ width: '150px' }}>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn"
                      onClick={() => {
                        setForm({
                          class_id: "",
                          section_id: "",
                          academic_year_id: ""
                        });
                        setStrengthData([]);
                        setIsSubmitted(false);
                      }}
                    >
                      Reset
                    </Button>

                    <Button
                      type="submit"
                      variant="success"
                      className="btn btn-success primaryBtn"
                    >
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <Button
              className='btnMain'
              variant="success"
              onClick={exportToExcel}
              style={{ marginRight: "10px", marginLeft: "1250px" }}
            >
              <img
                src={excelIcon}
                alt="Download Excel"
                style={{ width: "20px" }}
              />
            </Button>
            <Button
              className="btnMain"
              variant="danger"
              onClick={exportToPDF}
              title="Export to PDF"
            >
              <FaFilePdf style={{ width: "20px" }} />
            </Button>
          </div>
        </div>

        <div className="commonTable height100 mt-2">
          <div className="tableBody">
            <div style={{ width: "100%", overflowX: "auto" }}>
              <AppDataGrid
                rows={strengthData.length ? strengthData : []}
                columns={columns}
                getRowId={(row) =>
                  row.section_id ||
                  row.class_id ||
                  `${row.class_name}_${row.section_name}`
                }
                pageSizeOptions={[10, 20, 50]}
                showToolbar={false}
                pagination
                density="compact"
                maxHeight={400}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SchoolStrength;
